import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useRouter } from 'next/router';

/**
 * @name 아이프레임 내 자손에게 상태 데이터를 100ms 초 단위로 받아서 높이를 갱신하는 컴포넌트
 *
 * @description
 * 아이프레임 내 자손의 함수에게서 높이를 전달 받습니다. 자손에서 `sendIframeSizeToParent()` 함수를 실행하면 이 컴포넌트에서 응답 받은 뒤 높이 상태 값을 업데이트 후 렌더링합니다.
 */
export const IframeResizer = (props: IframeResizerProps) => {
  const {
    src
  } = props;
  const {
    locale
  } = useRouter();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(0);
  const handleIframeHeightChange = (e: MessageEvent<{
    scrollHeight: number;
    loaded: boolean;
  }>) => {
    if (Object.keys(e.data).includes('scrollHeight')) {
      setIframeHeight(e.data.scrollHeight || 0);
    }
  };
  useEffect(() => {
    window.addEventListener('message', handleIframeHeightChange);
    return () => window.removeEventListener('message', handleIframeHeightChange);
  }, []);
  useEffect(() => {
    if (!iframeRef.current || iframeHeight <= 0) {
      return;
    }
    const childData = {
      height: iframeHeight,
      loaded: true
    };
    iframeRef.current.contentWindow?.postMessage(childData, '*');
  }, [iframeHeight]);
  const {
    data
  } = useQuery({
    queryKey: ['iframe', src],
    queryFn: async () => {
      const response = await axios.get(src, {
        headers: {
          'accept-language': locale
        },
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    }
  });
  return <iframe ref={iframeRef} width="100%" height={iframeHeight} src={data} data-sentry-component="IframeResizer" data-sentry-source-file="IframeResizer.tsx" />;
};
type IframeResizerProps = {
  src: string;
};